<template>
  <div>
    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />操作流水
          <span class="adm-card-title-extra">共 {{ total }} 条 · 只读，不可修改或删除</span>
        </div>
      </div>

      <div class="adm-filter">
        <el-select v-model="query.action" class="adm-w200" placeholder="全部动作" clearable>
          <el-option v-for="a in actions" :key="a.code" :label="a.desc" :value="a.code" />
        </el-select>
        <el-input
          v-model="query.adminId"
          class="adm-w180"
          name="adminId"
          autocomplete="off"
          placeholder="操作人 ID"
          clearable
          @keyup.enter.native="onSearch"
        />
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="onSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onReset">重置</el-button>
      </div>

      <el-table v-loading="loading" :data="rows" border>
        <el-table-column label="时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作人" width="170">
          <template slot-scope="{ row }">
            {{ row.adminName || '-' }}
            <span class="adm-sub">{{ row.adminId }}</span>
          </template>
        </el-table-column>
        <el-table-column label="动作" width="130">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="actionTagType(row.action)">
              {{ row.actionDesc || row.action }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="对象" width="200">
          <template slot-scope="{ row }">
            <span v-if="row.targetType">{{ row.targetType }} / {{ row.targetId }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="detail" label="详情" min-width="240" show-overflow-tooltip />
        <el-table-column prop="ip" label="IP" width="140" />
      </el-table>

      <AdmPager
        :total="total"
        :page="query.page"
        :size="query.size"
        @page-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>
  </div>
</template>

<script>
import { fetchOperationLogs, isOk } from '@/api/admin'
import AdmPager from '@/components/admin/AdmPager.vue'

/**
 * 动作码与中文名必须与后端 AdminAction 枚举保持一致。
 * 后端对未知 action 不报错（等同不过滤），所以这里多传/少传都不会炸。
 */
const ACTIONS = [
  { code: 'LOGIN', desc: '登录' },
  { code: 'LOGOUT', desc: '登出' },
  { code: 'CHANGE_PASSWORD', desc: '修改密码' },
  { code: 'USER_DISABLE', desc: '禁用用户' },
  { code: 'USER_ENABLE', desc: '启用用户' },
  { code: 'USER_UPDATE', desc: '修改用户资料' },
  { code: 'CONTENT_HIDE', desc: '下架内容' },
  { code: 'CONTENT_RESTORE', desc: '恢复内容' },
  { code: 'CONTENT_UPDATE', desc: '修改内容' },
  { code: 'INVITE_GENERATE', desc: '生成邀请码' },
  { code: 'INVITE_DISABLE', desc: '禁用邀请码' },
  { code: 'INVITE_ENABLE', desc: '启用邀请码' },
  { code: 'INVITE_DELETE', desc: '删除邀请码' },
  { code: 'SETTING_UPDATE', desc: '修改系统设置' },
  { code: 'SETTING_RESET', desc: '恢复系统设置默认值' },
  { code: 'ACHIEVEMENT_CREATE', desc: '新增成就' },
  { code: 'ACHIEVEMENT_UPDATE', desc: '编辑成就' },
  { code: 'ACHIEVEMENT_DELETE', desc: '删除成就' },
  { code: 'ACHIEVEMENT_ENABLE', desc: '上架成就' },
  { code: 'ACHIEVEMENT_DISABLE', desc: '下架成就' },
  { code: 'APP_VERSION_CREATE', desc: '新增 APP 版本' },
  { code: 'APP_VERSION_UPDATE', desc: '编辑 APP 版本' },
  { code: 'APP_VERSION_STATUS', desc: 'APP 版本状态流转' },
  { code: 'APP_VERSION_DELETE', desc: '删除 APP 版本' },
  { code: 'APP_FLAG_CREATE', desc: '新增功能开关' },
  { code: 'APP_FLAG_UPDATE', desc: '编辑功能开关' },
  { code: 'APP_FLAG_ENABLE', desc: '切换功能开关' },
  { code: 'APP_FLAG_DELETE', desc: '删除功能开关' },
  { code: 'APP_POLICY_UPDATE', desc: '保存 APP 平台策略' }
]

export default {
  name: 'LogsPanel',
  components: { AdmPager },
  data() {
    return {
      actions: ACTIONS,
      query: { action: '', adminId: '', page: 1, size: 20 },
      rows: [],
      total: 0,
      loading: false
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      fetchOperationLogs(this.query)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '查询失败')
          }
          this.rows = res.data.list || []
          this.total = Number(res.data.total || 0)
        })
        .catch(err => {
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSearch() {
      this.query.page = 1
      this.load()
    },
    onReset() {
      this.query = { action: '', adminId: '', page: 1, size: 20 }
      this.load()
    },
    onPageChange(page) {
      this.query.page = page
      this.load()
    },
    onSizeChange(size) {
      this.query.size = size
      this.query.page = 1
      this.load()
    },
    actionTagType(action) {
      if (!action) return 'info'
      if (action.indexOf('INVITE') === 0) return 'success'
      if (action.indexOf('SETTING') === 0) return 'success'
      if (action.indexOf('CONTENT') === 0) return 'warning'
      if (action.indexOf('USER') === 0) return 'danger'
      return 'info'
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
/* 卡片 / 筛选栏 / 分页样式统一来自 @/styles/admin.css */
.adm-w200 {
  width: 200px;
}

.adm-w180 {
  width: 180px;
}
</style>
